import { graphql, type ResultOf, type VariablesOf } from '@newfront-insurance/graphql-frontend';

export const GET_NOVA_ANALYTICS_TEAMS = graphql(`
  query GetNovaAnalyticsTeams {
    getNovaAnalyticsTeams {
      id
      name
    }
  }
`);

const NOVA_ANALYTICS_KPI_FIELDS = graphql(`
  fragment NovaAnalyticsKpiFields on NovaAnalyticsKpi @_unmask {
    value
    secondaryValue
    delta {
      previousValue
      label
      tone
    }
  }
`);

const NOVA_ANALYTICS_KPIS_FIELDS = graphql(
  `
    fragment NovaAnalyticsKpisFields on NovaAnalyticsKpis @_unmask {
      asOfDate
      isPreview
      workingDayUsageRate {
        ...NovaAnalyticsKpiFields
      }
      activePeopleThisWeek {
        ...NovaAnalyticsKpiFields
      }
      skillsBuiltThisQuarter {
        ...NovaAnalyticsKpiFields
      }
      dormantSharedSkills {
        ...NovaAnalyticsKpiFields
      }
    }
  `,
  [NOVA_ANALYTICS_KPI_FIELDS],
);

export const GET_NOVA_ANALYTICS_KPIS = graphql(
  `
    query GetNovaAnalyticsKpis($scope: NovaAnalyticsScopeInput) {
      getNovaAnalyticsKpis(scope: $scope) {
        ...NovaAnalyticsKpisFields
      }
    }
  `,
  [NOVA_ANALYTICS_KPIS_FIELDS],
);

export type NovaAnalyticsKpis = NonNullable<ResultOf<typeof GET_NOVA_ANALYTICS_KPIS>['getNovaAnalyticsKpis']>;
export type NovaAnalyticsKpi = NovaAnalyticsKpis['workingDayUsageRate'];

const NOVA_SKILL_USAGE_PAGE_FIELDS = graphql(`
  fragment NovaSkillUsagePageFields on NovaSkillUsagePage @_unmask {
    totalCount
    asOfDate
    isPreview
    items {
      skillKey
      skillName
      ownerName
      shareState
      runnerCount30d
      runCount30d
      lastRunAt
    }
  }
`);

export const GET_NOVA_SKILL_USAGE = graphql(
  `
    query GetNovaSkillUsage(
      $scope: NovaAnalyticsScopeInput
      $filter: NovaSkillUsageFilterInput
      $sortField: NovaSkillUsageSortField
      $sortDirection: NovaAnalyticsSortDirection
      $page: NovaAnalyticsPageInput
    ) {
      getNovaSkillUsage(
        scope: $scope
        filter: $filter
        sortField: $sortField
        sortDirection: $sortDirection
        page: $page
      ) {
        ...NovaSkillUsagePageFields
      }
    }
  `,
  [NOVA_SKILL_USAGE_PAGE_FIELDS],
);

const NOVA_PERSON_USAGE_PAGE_FIELDS = graphql(`
  fragment NovaPersonUsagePageFields on NovaPersonUsagePage @_unmask {
    totalCount
    asOfDate
    isPreview
    items {
      personKey
      fullName
      roleLabel
      messageCount30d
      skillsUsedCount30d
      skillsUsed
      lastUsedAt
    }
  }
`);

export const GET_NOVA_PERSON_USAGE = graphql(
  `
    query GetNovaPersonUsage(
      $scope: NovaAnalyticsScopeInput
      $search: String
      $sortField: NovaPersonUsageSortField
      $sortDirection: NovaAnalyticsSortDirection
      $page: NovaAnalyticsPageInput
    ) {
      getNovaPersonUsage(
        scope: $scope
        search: $search
        sortField: $sortField
        sortDirection: $sortDirection
        page: $page
      ) {
        ...NovaPersonUsagePageFields
      }
    }
  `,
  [NOVA_PERSON_USAGE_PAGE_FIELDS],
);

const NOVA_INACTIVE_PEOPLE_PAGE_FIELDS = graphql(`
  fragment NovaInactivePeoplePageFields on NovaInactivePersonPage @_unmask {
    totalCount
    asOfDate
    isPreview
    rosterAvailable
    items {
      personKey
      fullName
      roleLabel
      accessGrantedAt
      lastUsedAt
      inactiveDays
    }
  }
`);

export const GET_NOVA_INACTIVE_PEOPLE = graphql(
  `
    query GetNovaInactivePeople(
      $scope: NovaAnalyticsScopeInput
      $search: String
      $sortField: NovaInactivePersonSortField
      $sortDirection: NovaAnalyticsSortDirection
      $page: NovaAnalyticsPageInput
    ) {
      getNovaInactivePeople(
        scope: $scope
        search: $search
        sortField: $sortField
        sortDirection: $sortDirection
        page: $page
      ) {
        ...NovaInactivePeoplePageFields
      }
    }
  `,
  [NOVA_INACTIVE_PEOPLE_PAGE_FIELDS],
);

export const NOVA_ANALYTICS_INITIAL_PAGE = { offset: 0, limit: 6 };

// Match the tables' initial arguments so their individual queries can read this response from Apollo's cache.
export const GET_NOVA_ANALYTICS_DASHBOARD = graphql(
  `
    query GetNovaAnalyticsDashboard($scope: NovaAnalyticsScopeInput, $page: NovaAnalyticsPageInput!) {
      getNovaAnalyticsTeams {
        id
        name
      }
      getNovaAnalyticsKpis(scope: $scope) {
        ...NovaAnalyticsKpisFields
      }
      getNovaSkillUsage(
        scope: $scope
        filter: { search: "", shareState: null }
        sortField: RUN_COUNT
        sortDirection: DESC
        page: $page
      ) {
        ...NovaSkillUsagePageFields
      }
      getNovaPersonUsage(scope: $scope, search: "", sortField: MESSAGE_COUNT, sortDirection: DESC, page: $page) {
        ...NovaPersonUsagePageFields
      }
      getNovaInactivePeople(scope: $scope, search: "", sortField: LAST_USED_AT, sortDirection: ASC, page: $page) {
        ...NovaInactivePeoplePageFields
      }
    }
  `,
  [
    NOVA_ANALYTICS_KPIS_FIELDS,
    NOVA_SKILL_USAGE_PAGE_FIELDS,
    NOVA_PERSON_USAGE_PAGE_FIELDS,
    NOVA_INACTIVE_PEOPLE_PAGE_FIELDS,
  ],
);

export type NovaAnalyticsDashboardData = ResultOf<typeof GET_NOVA_ANALYTICS_DASHBOARD>;

export type NovaSkillUsageRow = NonNullable<
  ResultOf<typeof GET_NOVA_SKILL_USAGE>['getNovaSkillUsage']
>['items'][number];
export type NovaPersonUsageRow = NonNullable<
  ResultOf<typeof GET_NOVA_PERSON_USAGE>['getNovaPersonUsage']
>['items'][number];
export type NovaInactivePersonRow = NonNullable<
  ResultOf<typeof GET_NOVA_INACTIVE_PEOPLE>['getNovaInactivePeople']
>['items'][number];
export type NovaSkillUsageVariables = VariablesOf<typeof GET_NOVA_SKILL_USAGE>;
export type NovaPersonUsageVariables = VariablesOf<typeof GET_NOVA_PERSON_USAGE>;
export type NovaInactivePeopleVariables = VariablesOf<typeof GET_NOVA_INACTIVE_PEOPLE>;
